import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';
import { attachId, attachIds } from '../utils';

import { Tutorial, TutorialScope } from './types';

export const tutorialsApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    getTutorial: build.query<CognitoAPIResponse<Tutorial> | undefined,
    {course: string, id: string}>({
      query: (arg) => {
        const { course, id } = arg;
        return {
          url: `tutorials/${course}/${encodeURIComponent(id)}`,
          method: 'GET',
        };
      },
      providesTags: (result) => [{ type: 'Tutorials', id: result?.data.id }],
      transformResponse(response: any) {
        return attachId(response, (entry: any) => `${entry.course}#${entry.startDatetimeIdentifier}`);
      },
    }),
    getTutorialsInRange: build.query<CognitoAPIResponse<Tutorial[]>,
    {startDatetime: number, endDatetime: number, host?: string}>({
      query: (arg) => {
        const { startDatetime, endDatetime, host } = arg;
        return {
          url: 'tutorials/query',
          params: {
            startDatetime, endDatetime, host,
          },
          method: 'GET',
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'Tutorials' as const, id })), 'Tutorials']
        : ['Tutorials']),
      transformResponse(response: any) {
        return attachIds(response, (entry: any) => `${entry.course}#${entry.startDatetimeIdentifier}`);
      },
    }),
    bookTutorial: build.mutation<any,
    { course: string, id: string, studentId: string | undefined, note?: string | undefined}>({
      query: (arg) => {
        const { course, id, studentId, note } = arg;
        return {
          url: `tutorials/${course}/${encodeURIComponent(id)}/book`,
          body: {
            studentId,
            note,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg: any) => [{
        type: 'Tutorials', id: `${arg.course}#${arg.id}`,
      }, {
        type: 'Students', id: arg.studentId,
      }],
    }),
    unbookTutorial: build.mutation<any,
    { course: string, id: string, studentId: string | undefined}>({
      query: (arg) => {
        const { course, id, studentId } = arg;
        return {
          url: `tutorials/${course}/${encodeURIComponent(id)}/unbook`,
          body: {
            studentId,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg: any) => [{
        type: 'Tutorials', id: `${arg.course}#${arg.id}`,
      }, {
        type: 'Students', id: arg.studentId,
      }],
    }),
    batchUnbookTutorial: build.mutation<any,
    { course: string, id: string, studentIds: string[]}>({
      query: (arg) => {
        const { course, id, studentIds } = arg;
        return {
          url: `tutorials/${course}/${encodeURIComponent(id)}/batchUnbook`,
          body: {
            studentIds,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg: any) => [{ type: 'Tutorials', id: `${arg.course}#${arg.id}` },
        ...arg.studentIds.map((studentId: string) => ({ type: 'Students', id: studentId }))],
    }),
    deleteTutorial: build.mutation<any,
    { course: string, id: string, deleteScope?: TutorialScope}>({
      query: (arg) => {
        const { course, id, deleteScope } = arg;
        return {
          url: `tutorials/${course}/${encodeURIComponent(id)}`,
          method: 'DELETE',
          body: {
            deleteScope,
          },
        };
      },
      invalidatesTags: (result, error, arg: any) => [{ type: 'Tutorials', id: `${arg.course}#${arg.id}` }],
    }),
    newTutorial: build.mutation<any,
    { attributes: any }>({
      query: (arg) => {
        const { attributes } = arg;
        return {
          url: 'tutorials/new',
          body: attributes,
          method: 'POST',
        };
      },
      invalidatesTags: ['Tutorials'],
    }),
    updateTutorial: build.mutation<any,
    { course: string, id: string, attributes: any, updateScope?: TutorialScope }>({
      query: (arg) => {
        const { course, id, attributes, updateScope } = arg;
        return {
          url: `tutorials/${course}/${encodeURIComponent(id)}/update`,
          body: {
            ...attributes,
            updateScope,
          },
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg: any) => [{ type: 'Tutorials', id: `${arg.course}#${arg.id}` }],
    }),
  }),
});

export const {
  useLazyGetTutorialQuery,
  useGetTutorialQuery,
  useGetTutorialsInRangeQuery,
  useBatchUnbookTutorialMutation,
  useBookTutorialMutation,
  useUnbookTutorialMutation,
  useDeleteTutorialMutation,
  useNewTutorialMutation,
  useUpdateTutorialMutation,
} = tutorialsApi;
